/**
 * @fileoverview AR-924 — "Work completed on" control: `picker` (controlled, before the first upload) or
 * `summary` ("Work completed on: 08/20/2026 Edit", inline Save / Cancel; the draft lives here).
 */

import { useState } from 'react'
import { Anchor, Button, Group, Stack, Text } from '@mantine/core'
import { DateInput } from '@mantine/dates'
import { IconCalendar } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { dateInputFormat, formatNumericDate, fromIsoDay, toIsoDay } from './dateFormat'

/** Figma: 220px input, 16px calendar glyph, 6px between label and input. */
const INPUT_WIDTH = 220
const ICON_SIZE = 16
const LABEL_GAP = 6

interface WorkDateInputProps {
  readonly value: string | null
  readonly onPick: (isoDay: string | null) => void
}

function WorkDateInput({ value, onPick }: WorkDateInputProps) {
  const { t, i18n } = useTranslation()
  return (
    <DateInput
      value={fromIsoDay(value)}
      onChange={date => onPick(date ? toIsoDay(date) : null)}
      valueFormat={dateInputFormat(i18n.language)}
      icon={<IconCalendar size={ICON_SIZE} />}
      clearable={false}
      popoverProps={{ withinPortal: true }}
      sx={{ width: INPUT_WIDTH }}
      aria-label={t('task_tracker.teacher_upload.work_completed_on')}
    />
  )
}

interface WorkCompletedOnFieldProps {
  readonly mode: 'picker' | 'summary'
  /** `YYYY-MM-DD` */
  readonly value: string | null
  readonly saving?: boolean
  readonly onChange?: (isoDay: string) => void
  readonly onSave?: (isoDay: string) => Promise<boolean>
  /** Summary mode: true while an edit differs from the saved value. */
  readonly onEditingChange?: (dirty: boolean) => void
}

export function WorkCompletedOnField(props: WorkCompletedOnFieldProps) {
  const { mode, value, saving = false, onChange, onSave, onEditingChange } = props
  const { t, i18n } = useTranslation()
  const tr = (key: string) => t(`task_tracker.teacher_upload.${key}`)
  const [draft, setDraft] = useState<string | null>(null)
  const editing = draft !== null

  function pickInitialDate(next: string | null) {
    if (next) onChange?.(next)
  }

  function startEditing() {
    setDraft(value)
    onEditingChange?.(false)
  }

  function pickDraft(next: string | null) {
    setDraft(next)
    const isDirty = next !== value
    onEditingChange?.(isDirty)
  }

  function stopEditing() {
    setDraft(null)
    onEditingChange?.(false)
  }

  async function save() {
    if (!draft || !onSave) return
    const saved = await onSave(draft)
    if (saved) stopEditing()
  }

  if (mode === 'picker') {
    return (
      <Stack spacing={LABEL_GAP}>
        <Text size="sm" weight={600}>{tr('work_completed_on')}</Text>
        <WorkDateInput value={value} onPick={pickInitialDate} />
        <Text size="xs" color="dimmed">{tr('work_completed_help')}</Text>
      </Stack>
    )
  }

  if (!editing) {
    return (
      <Text size="sm">
        {tr('work_completed_on')}: <Text span weight={600}>{formatNumericDate(value, i18n.language)}</Text>{' '}
        <Anchor component="button" type="button" size="sm" weight={600} onClick={startEditing} disabled={saving}>
          {tr('edit')}
        </Anchor>
      </Text>
    )
  }

  const unchanged = !draft || draft === value
  return (
    <Group spacing="xs" align="flex-end">
      <Stack spacing={LABEL_GAP}>
        <Text size="sm" weight={600}>{tr('work_completed_on')}</Text>
        <WorkDateInput value={draft} onPick={pickDraft} />
      </Stack>
      <Button size="sm" onClick={save} loading={saving} disabled={unchanged}>{tr('save')}</Button>
      <Button size="sm" variant="default" onClick={stopEditing} disabled={saving}>{tr('cancel')}</Button>
    </Group>
  )
}
